/**
 * A refusal, worded.
 *
 * `assignmentRequest` returns refusals as DATA — a kind plus the dates or slots the sentence needs —
 * so the rule and the wording can be tested apart. This is the other half: the sentence, with its
 * dates formatted for the reader's locale.
 *
 * It lives beside the rules rather than in the dialog because a refusal that never reaches the
 * screen is the failure mode: the submit button is disabled and nothing says why. A key that does
 * not exist renders as a raw locale path in exactly that spot, which is why the test here walks
 * every kind.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. `t` and `locale` are parameters.
 */

import { formatCalendarDate } from '~/shared/lib/calendarDate';
import type { AssignBlocking } from './assignBlocking';
import type { AssignRefusal } from './assignRequest';
import type { DropRefusal } from './projectionDrop';
import type { OccurrenceEditRefusal } from './occurrenceEdit';

export type Translate = (key: string, values?: Record<string, string | number>) => string;

/** One refusal as a sentence. */
export function refusalMessage(refusal: AssignRefusal, locale: string, t: Translate): string {
  switch (refusal.kind) {
    case 'noClient':
      return t('web:assign.refusal.noClient');
    case 'noStartDate':
      return t('web:assign.refusal.noStartDate');
    case 'startTooEarly':
      return t('web:assign.refusal.startTooEarly', { date: formatCalendarDate(refusal.earliest, locale) });
    case 'startTooLate':
      return t('web:assign.refusal.startTooLate', { date: formatCalendarDate(refusal.latest, locale) });
    case 'noStationaryTimes':
      return t('web:assign.refusal.noStationaryTimes');
    case 'malformedTime':
      return t('web:assign.refusal.malformedTime', { time: refusal.time });
    case 'timeOffPattern':
      return t('web:assign.refusal.timeOffPattern');
    case 'overrideOutOfRange':
      return t('web:assign.refusal.overrideOutOfRange', {
        date: formatCalendarDate(refusal.date, locale),
        earliest: formatCalendarDate(refusal.earliest, locale),
        latest: formatCalendarDate(refusal.latest, locale),
      });
  }
}

/**
 * What the footer says beside a refused primary.
 *
 * The loading case is its own sentence and not a refusal: there is no target to judge a selection
 * against yet, so nothing `assignmentRequest` can produce describes it. Everything else is one of
 * the refusals above, worded by the one function that words them.
 */
export function assignBlockingMessage(
  blocking: AssignBlocking,
  locale: string,
  t: Translate,
): string {
  return blocking.kind === 'pending'
    ? t('web:assign.blocking.loading')
    : refusalMessage(blocking.refusal, locale, t);
}

/**
 * Every refusal, worded and de-duplicated.
 *
 * Two occurrences moved outside the window produce two `overrideOutOfRange` refusals with different
 * coordinates but, at this level of detail, one sentence — and the same sentence twice reads as a
 * rendering bug rather than as two problems.
 */
export function refusalMessages(
  refusals: readonly AssignRefusal[],
  locale: string,
  t: Translate,
): string[] {
  return [...new Set(refusals.map((refusal) => refusalMessage(refusal, locale, t)))];
}

/**
 * A refused DROP, worded — for the keyboard path and for the popover.
 *
 * A pointer drag refuses by never entering the target (view ADR §11) and says nothing, which is
 * right: a live region firing on every cell the pointer crosses is a torrent nobody can read
 * mid-gesture. The arrows are the opposite case — a trainer who aimed at a cell and pressed Space
 * asked the question — and so is a date typed into the popover. Both read these.
 */
export function dropRefusalMessage(refusal: DropRefusal, t: Translate): string {
  return t(`web:assign.drop.${refusal}`);
}

/** A refused popover edit, worded. Shares the two sentences a drop refuses in, plus its own two. */
export function occurrenceEditRefusalMessage(refusal: OccurrenceEditRefusal, t: Translate): string {
  return t(`web:assign.edit.refusal.${refusal}`);
}
